/**
 * Versioned BRSR applicability configuration.
 * Update this file when SEBI amends cohort, year or value-chain rules.
 * Do not scatter replacement thresholds through the UI.
 *
 * Status of this file: last reviewed 2026-10-13 against the March 2025 circular
 * SEBI/HO/CFD/CFD-PoD-1/P/CIR/2025/42 and the July 2023 circular it amends.
 * A later circular was not identified. See regulatorySources.ts.
 */

export const REGULATORY_CONFIG_VERSION = '2026.10.13'

export interface YearCohortRule {
  financialYear: string
  topListedEntities: number
  sourceId: string
}

export const REGULATORY_RULES = {
  version: REGULATORY_CONFIG_VERSION,
  lastReviewed: '2026-10-13',
  brsrFiling: {
    fromFinancialYear: '2022-23',
    topListedEntities: 1000,
    sourceId: 'sebi-2021-brsr',
    note: 'Mandatory BRSR applies to the top 1,000 listed entities by market capitalisation. It is not a universal obligation for every Indian company.',
  },
  updatedBrsrFormat: {
    fromFinancialYear: '2023-24',
    topListedEntities: 1000,
    sourceId: 'sebi-2023-core',
    note: 'From FY 2023-24 the top 1,000 listed entities make disclosures as per the updated BRSR format, which incorporates BRSR Core KPIs.',
  },
  /**
   * Mandatory assessment or assurance of BRSR Core.
   * March 2025 restated this glide path and replaced “reasonable assurance”
   * with “assessment or assurance”.
   */
  coreAssessmentOrAssurance: [
    { financialYear: '2023-24', topListedEntities: 150, sourceId: 'sebi-2025-ease' },
    { financialYear: '2024-25', topListedEntities: 250, sourceId: 'sebi-2025-ease' },
    { financialYear: '2025-26', topListedEntities: 500, sourceId: 'sebi-2025-ease' },
    { financialYear: '2026-27', topListedEntities: 1000, sourceId: 'sebi-2025-ease' },
  ] satisfies YearCohortRule[],
  valueChainDisclosures: {
    fromFinancialYear: '2025-26',
    topListedEntities: 250,
    basis: 'voluntary' as const,
    sourceId: 'sebi-2025-ease',
    note: 'Voluntary for the top 250 listed entities from FY 2025-26. The July 2023 comply-or-explain timetable is not the current rule. For the first year of value-chain reporting, previous-year numbers are voluntary.',
  },
  valueChainAssessmentOrAssurance: {
    fromFinancialYear: '2026-27',
    topListedEntities: 250,
    basis: 'voluntary' as const,
    sourceId: 'sebi-2025-ease',
    note: 'Voluntary from FY 2026-27. Not a mandatory assurance obligation.',
  },
  greenCredits: {
    basis: 'voluntary_leadership' as const,
    principle: 'P6',
    fromFinancialYear: '2024-25',
    sourceId: 'sebi-2025-ease',
    note: 'Voluntary leadership-indicator disclosure on green credits. Not scored as a mandatory requirement.',
  },
  valueChainPartnerThreshold: {
    status: 'requires_review' as const,
    note: 'The March 2025 circular says the value-chain partner threshold was revised. The exact replacement wording was not fully extracted in this review, so the tool does not apply a numeric partner cut-off.',
  },
  filingDeadline: {
    status: 'not_encoded' as const,
    note: 'BRSR is filed with the annual report. A specific calendar deadline is not encoded here because the LODR filing timetable was not re-verified for this configuration. Confirm the date with the current LODR Regulations and the exchange.',
  },
  segmentCaveats: {
    sme: 'An SME-platform listing is not treated as automatically inside or outside the top 1,000 market-capitalisation cohort. Confirm the ranking before relying on a filing conclusion.',
    debt_only:
      'A debt-only listing does not map directly onto the equity market-capitalisation cohort used for BRSR. Further verification is required.',
  },
  rankingDisclaimer:
    'This tool does not read NSE or BSE market-capitalisation lists. Cohort conclusions use only the range you select.',
}

/** Mutually exclusive cohort answers. A company in the top 150 is not also tagged as top 1,000-only. */
export const COHORT_MAX_RANK = {
  top_150: 150,
  top_151_250: 250,
  top_251_500: 500,
  top_501_1000: 1000,
  outside_top_1000: 'outside',
  not_sure: 'unknown',
} as const

export type CohortAnswer = keyof typeof COHORT_MAX_RANK

const FY_START: Record<string, number> = {
  '2022-23': 2022,
  '2023-24': 2023,
  '2024-25': 2024,
  '2025-26': 2025,
  '2026-27': 2026,
}

const ANSWER_TO_FY: Record<string, string> = {
  fy2023_24: '2023-24',
  fy2024_25: '2024-25',
  fy2025_26: '2025-26',
  fy2026_27: '2026-27',
}

export function financialYearKey(answer: string | undefined): string | null {
  if (!answer) return null
  return ANSWER_TO_FY[answer] ?? null
}

export function financialYearLabel(answer: string | undefined): string {
  const key = financialYearKey(answer)
  return key ? `FY ${key}` : 'Financial year not selected'
}

export function fyStartYear(fyKey: string): number | null {
  return FY_START[fyKey] ?? null
}

export function isFyOnOrAfter(fyKey: string, fromFy: string): boolean | 'unknown' {
  const year = fyStartYear(fyKey)
  const from = fyStartYear(fromFy)
  if (year == null || from == null) return 'unknown'
  return year >= from
}

export function isWithinTop(cohort: string | undefined, topN: number): boolean | 'unknown' {
  if (!cohort) return 'unknown'
  const rank = COHORT_MAX_RANK[cohort as CohortAnswer]
  if (rank === undefined || rank === 'unknown') return 'unknown'
  if (rank === 'outside') return false
  return rank <= topN
}

export function coreAssuranceLimit(fyKey: string): YearCohortRule | undefined {
  return REGULATORY_RULES.coreAssessmentOrAssurance.find((row) => row.financialYear === fyKey)
}
